import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Button } from '../../components/Shared/Button';
import { Card } from '../../components/Shared/Card';
import { Badge } from '../../components/Shared/Badge';
import { EmptyState } from '../../components/Shared/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/Shared/Table';
import { useMeSupportStore } from '../../store/useMeSupportStore';
import { useAuthStore } from '../../store/useAuthStore';
import { SupportTicketStatus, SupportTicketType } from '../../api/types/admin';
import { formatDate } from '../../utils/dateUtils';
import { isUnread } from '../../utils/supportReadMarker';
import { MeSupportCreateModal } from './MeSupportCreateModal';

type Tab = 'open' | 'closed' | 'all';

const TYPE_KEY: Record<number, string> = {
  [SupportTicketType.Question]: 'question',
  [SupportTicketType.Problem]: 'problem',
  [SupportTicketType.NewTenantRequest]: 'question',
  [SupportTicketType.MeetingRequest]: 'meetingRequest',
  [SupportTicketType.Other]: 'other',
};

function typeColor(typ: number | undefined): 'gray' | 'blue' | 'red' | 'purple' {
  switch (typ) {
    case SupportTicketType.Problem:
      return 'red';
    case SupportTicketType.MeetingRequest:
      return 'purple';
    case SupportTicketType.Question:
      return 'blue';
    default:
      return 'gray';
  }
}

export default function MeSupportInboxPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['support', 'common']);
  const tenantId = useAuthStore((s) => s.employee?.tenantId ?? '');
  const { tickets, filters, loading, error, setFilters, fetchAll } = useMeSupportStore();

  const [tab, setTab] = useState<Tab>('open');
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    fetchAll();
    // Polling: catch admin replies while the user lingers on the inbox.
    const id = window.setInterval(() => fetchAll(), 60_000);
    return () => window.clearInterval(id);
  }, [fetchAll]);

  const visibleTickets = useMemo(() => {
    if (tab === 'all') return tickets;
    if (tab === 'closed') return tickets.filter((t0) => t0.status === SupportTicketStatus.Closed);
    return tickets.filter((t0) => t0.status !== SupportTicketStatus.Closed);
  }, [tickets, tab]);

  const openCount = useMemo(
    () => tickets.filter((t0) => t0.status !== SupportTicketStatus.Closed).length,
    [tickets],
  );
  const closedCount = useMemo(
    () => tickets.filter((t0) => t0.status === SupportTicketStatus.Closed).length,
    [tickets],
  );

  const tabBtn = (active: boolean) =>
    `px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors ${
      active ? 'bg-[#00A871] text-white' : 'text-slate-600 hover:bg-slate-100'
    }`;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('support:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{t('support:subtitle')}</p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
          >
            {t('support:actions.create')}
          </Button>
        </div>
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <button type="button" className={tabBtn(tab === 'open')} onClick={() => setTab('open')}>
              {t('support:tabs.open')}
              {openCount > 0 && tab !== 'open' && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-slate-200 text-slate-700 text-[10px]">
                  {openCount}
                </span>
              )}
            </button>
            <button type="button" className={tabBtn(tab === 'closed')} onClick={() => setTab('closed')}>
              {t('support:tabs.closed')}
              {closedCount > 0 && tab !== 'closed' && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-slate-200 text-slate-700 text-[10px]">
                  {closedCount}
                </span>
              )}
            </button>
            <button type="button" className={tabBtn(tab === 'all')} onClick={() => setTab('all')}>
              {t('support:tabs.all')}
            </button>
          </div>

          <div className="ml-auto">
            <select
              value={filters.type ?? 'all'}
              onChange={(e) => {
                const v = e.target.value;
                setFilters({
                  type: v === 'all' ? undefined : (Number(v) as SupportTicketType),
                });
                fetchAll();
              }}
              className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              <option value="all">{t('support:filters.all')}</option>
              <option value={SupportTicketType.Question}>{t('support:types.question')}</option>
              <option value={SupportTicketType.Problem}>{t('support:types.problem')}</option>
              <option value={SupportTicketType.MeetingRequest}>{t('support:types.meetingRequest')}</option>
              <option value={SupportTicketType.Other}>{t('support:types.other')}</option>
            </select>
          </div>
        </div>
      </Card>

      {error ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('common:errors.loadFailed')}
            onRetry={fetchAll}
          />
        </Card>
      ) : loading && tickets.length === 0 ? (
        <Card>
          <p className="text-[13px] text-slate-500 text-center py-6">{t('common:actions.loading')}</p>
        </Card>
      ) : visibleTickets.length === 0 ? (
        <Card>
          <EmptyState
            title={t('support:empty.title')}
            description={t('support:empty.description')}
            action={
              <Button
                onClick={() => setCreateOpen(true)}
                leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
              >
                {t('support:actions.create')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card noPadding>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader className="w-6"><span className="sr-only">·</span></TableHeader>
                <TableHeader>{t('support:fields.date')}</TableHeader>
                <TableHeader>{t('support:fields.type')}</TableHeader>
                <TableHeader>{t('support:fields.subject')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTickets.map((tkt) => (
                <TableRow key={tkt.id} onClick={() => tkt.id && navigate(`/support/${tkt.id}`)}>
                  <TableCell>
                    {isUnread(tkt, tenantId) && (
                      <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums text-slate-700">
                    {formatDate(tkt.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Badge color={typeColor(tkt.type)}>
                      {t(`support:types.${TYPE_KEY[tkt.type ?? -1] ?? 'other'}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">{tkt.subject}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <MeSupportCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => navigate(`/support/${id}`)}
      />
    </div>
  );
}
